/**
 * Collection-wide summary strip on the Datasets tab.
 *
 * Renders a 4-stat row (golden / in-progress / awaiting / progress %),
 * a stacked bar with legend, and a contextual "Next step" CTA whose
 * copy depends on the active tab. CTA renders only on the
 * `datasets`, `split`, and `upload` tabs (per `feature-spec.md` §2.3);
 * other tabs receive no CTA. Copy is verbatim from the spec.
 */

import React from 'react';

import { Alert, AlertDescription } from '../../../../../ui/alert';
import { Card, CardContent } from '../../../../../ui/card';
import StackedSegmentBar from '../bars/StackedSegmentBar';

export type SummaryTabKey =
  | 'datasets'
  | 'split'
  | 'overview'
  | 'coverage'
  | 'quality'
  | 'upload';

const CTA_COPY: Partial<Record<SummaryTabKey, string>> = {
  datasets: 'Upload new data or proceed to annotate existing records.',
  split: 'Split configured — run evaluation to test model performance.',
  upload: 'Dataset uploaded — start annotating records.',
};

export interface CollectionSummaryStats {
  totalRecords: number;
  golden: number;
  inProgress: number;
  awaiting: number;
  targetSum: number;
  progressPct: number;
}

interface Props {
  summary: CollectionSummaryStats;
  tabKey: SummaryTabKey;
}

type StatTone = 'success' | 'warning' | 'muted' | 'default';

const STAT_TONE_CLASS: Record<StatTone, string> = {
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  muted: 'text-muted-foreground',
  default: 'text-foreground',
};

const Stat: React.FC<{
  value: React.ReactNode;
  label: string;
  sub: string;
  tone?: StatTone;
}> = ({ value, label, sub, tone = 'default' }) => (
  <Card>
    <CardContent className="p-5 text-center">
      <div
        className={`text-2xl font-bold leading-none tabular-nums ${STAT_TONE_CLASS[tone]}`}
      >
        {value}
      </div>
      <div className="mt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>
    </CardContent>
  </Card>
);

const CollectionSummaryStrip: React.FC<Props> = ({ summary, tabKey }) => {
  const cta = CTA_COPY[tabKey];
  return (
    <div className="space-y-4" data-testid="collection-summary-strip">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat
          value={summary.golden.toLocaleString()}
          label="Golden Dataset"
          sub="Available for eval"
          tone="success"
        />
        <Stat
          value={summary.inProgress.toLocaleString()}
          label="In Progress"
          sub="Annotated, not finalised"
          tone="warning"
        />
        <Stat
          value={summary.awaiting.toLocaleString()}
          label="Awaiting Annotation"
          sub="Not yet usable"
          tone="muted"
        />
        <Stat
          value={`${summary.progressPct}%`}
          label="Progress to Target"
          sub={`${summary.golden.toLocaleString()} / ${summary.targetSum.toLocaleString()}`}
        />
      </div>
      <div className="space-y-2">
        <StackedSegmentBar
          golden={summary.golden}
          inProgress={summary.inProgress}
          awaiting={summary.awaiting}
        />
        <div className="flex flex-wrap gap-4 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-600" />
            Golden dataset
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-amber-600" />
            In progress
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-slate-200" />
            Awaiting annotation
          </span>
        </div>
      </div>
      {cta && (
        <Alert data-testid="next-step-cta">
          <AlertDescription>{cta}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default CollectionSummaryStrip;
